import { forwardRef, useContext, useMemo, useRef, useState, useEffect } from 'react'
import UUIThemeContext from '../theme/context'

import { UUIInputProps, UUIInputVariant } from '../types/UUIInput'

const UUIInput = forwardRef<HTMLInputElement, UUIInputProps>(
  (
    {
      uiInvalid,
      uiWarning,
      uiSize = 'normal',
      uiStyle = 'filled',
      uiSkin = 'default',
      uiLeftIcon,
      uiRightIcon,
      uiLeftAddons,
      uiRightAddons,
      className,
      maxLength,
      value,
      uiOnLeftIconClick,
      uiOnRightIconClick,
      disabled,
      readOnly,
      uiDeleteTextIcon = false,
      uiRequired,
      onChange,
      ...HTMLProps
    },
    ref
  ) => {
    const theme = useContext(UUIThemeContext)
    const themeVariant: UUIInputVariant = uiInvalid ? 'invalid' : uiWarning ? 'warning' : 'default'
    const [currentValue, setCurrentValue] = useState(value || '')
    const charsLeft = maxLength ? maxLength - (currentValue ? String(currentValue).length : 0) : null

    const inputRef = useRef<HTMLInputElement>(null)

    const forwardRef = (el: HTMLInputElement) => {
      inputRef.current = el
      if ('function' === typeof ref) {
        ref(el)
      } else if (ref) {
        ref.current = el
      }
    }

    useEffect(() => {
      setCurrentValue(value || '')
    }, [value])

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.currentTarget.value
      setCurrentValue(newValue)
      if (onChange) {
        onChange(event)
      }
    }

    const handleClear = () => {
      setCurrentValue('')

      if (inputRef.current) {
        inputRef.current.value = ''
        const inputEvent = new Event('input', { bubbles: true })
        inputRef.current.dispatchEvent(inputEvent)
      }
    }

    const themeClassNames = useMemo(() => {
      const style = uiStyle
      const size = uiSize
      const variant = themeVariant
      const skin = uiSkin

      const element =
        'function' === typeof theme?.Input?.element
          ? theme.Input.element({ style, size, variant, skin })
          : 'object' === typeof theme?.Input?.element
          ? theme.Input.element[style]?.[size]?.[variant] || ''
          : ''

      const input =
        'function' === typeof theme?.Input?.input
          ? theme.Input.input({ style, size, variant, skin })
          : 'object' === typeof theme?.Input?.input
          ? theme.Input.input[style]?.[size]?.[variant] || ''
          : ''

      const leftIcon =
        'function' === typeof theme?.Input?.leftIcon
          ? theme.Input.leftIcon({ style, size, variant, skin })
          : 'object' === typeof theme?.Input?.leftIcon
          ? theme.Input.leftIcon[style]?.[size]?.[variant] || ''
          : ''

      const rightIcon =
        'function' === typeof theme?.Input?.rightIcon
          ? theme.Input.rightIcon({ style, size, variant, skin })
          : 'object' === typeof theme?.Input?.rightIcon
          ? theme.Input.rightIcon[style]?.[size]?.[variant] || ''
          : ''

      const counter =
        'function' === typeof theme?.Input?.counter
          ? theme.Input.counter({ style, size, variant, skin })
          : 'object' === typeof theme?.Input?.counter
          ? theme.Input.counter[style]?.[size]?.[variant] || ''
          : ''

      const leftAddons =
        'function' === typeof theme?.Input?.leftAddons
          ? theme.Input.leftAddons({ style, size, variant, skin })
          : 'object' === typeof theme?.Input?.leftAddons
          ? theme.Input.leftAddons[style]?.[size]?.[variant] || ''
          : ''

      const rightAddons =
        'function' === typeof theme?.Input?.rightAddons
          ? theme.Input.rightAddons({ style, size, variant, skin })
          : 'object' === typeof theme?.Input?.rightAddons
          ? theme.Input.rightAddons[style]?.[size]?.[variant] || ''
          : ''

      return { element, input, leftIcon, rightIcon, counter, leftAddons, rightAddons }
    }, [theme, themeVariant, uiStyle, uiSize, uiSkin])

    return (
      <span
        className={` ${themeClassNames.element} ${className || ''}`}
        aria-disabled={disabled || readOnly ? true : undefined}
        onClick={disabled || readOnly ? undefined : () => void inputRef.current?.focus()}
        data-uuiinput-root
      >
        {uiLeftIcon && (
          <span
            data-uuiinput-left-icon
            className={`${themeClassNames.leftIcon} ${uiLeftIcon}`}
            onClick={uiOnLeftIconClick}
            role={uiOnLeftIconClick ? 'button' : undefined}
          />
        )}
        {uiLeftAddons && (
          <span data-uuiinput-left-addons className={themeClassNames.leftAddons}>
            {uiLeftAddons}
          </span>
        )}
        <input
          ref={forwardRef}
          className={themeClassNames.input}
          value={currentValue}
          maxLength={maxLength}
          onInput={handleChange}
          disabled={disabled}
          readOnly={readOnly}
          {...HTMLProps}
          data-uuiinput-main
        />
        {uiRightAddons && (
          <span data-uuiinput-right-addons className={themeClassNames.rightAddons}>
            {uiRightAddons}
          </span>
        )}
        {uiDeleteTextIcon && String(currentValue)?.length > 0 ? (
          <span data-uuiinput-delete-text-icon className={`${themeClassNames.rightIcon} tw:flex`} onClick={handleClear}>
            <i className="icon-aytmfd-close-v2" />
          </span>
        ) : null}
        {uiRequired && (
          <span
            data-uuiinput-required
            className={`tw:ml-2 tw:absolute tw:right-3 tw:top-[8px] tw:text-red-200 tw:text-[24px]`}
          >
            *
          </span>
        )}
        {null != charsLeft && !uiRequired && (
          <span data-uuiinput-counter className={`${themeClassNames.counter}`} onMouseDown={e => e.preventDefault()}>
            {charsLeft}
          </span>
        )}
        {uiRightIcon && (String(currentValue)?.length === 0 || !currentValue) ? (
          <span
            data-uuiinput-right-icon
            className={`${themeClassNames.rightIcon} ${uiRightIcon}`}
            onClick={uiOnRightIconClick}
            role={uiOnRightIconClick ? 'button' : undefined}
          />
        ) : null}
      </span>
    )
  }
)

UUIInput.displayName = 'UUIInput'

export default UUIInput
